import React, { useRef, useEffect, useState } from "react";
import * as echarts from "echarts";
import "./DonutChart.scss";
import { Tooltip } from "antd";

const DonutChart = ({
  data = [],
  labels = [],
  colors = [],
  total,
  centerLabel,
  title,
  chartId,
}) => {
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  const [truncateWidth, setTruncateWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setTruncateWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const shouldTruncate =
    (truncateWidth >= 1300 && truncateWidth <= 1790) || truncateWidth < 830;

  const [selectedIndex, setSelectedIndex] = useState(
    labels.findIndex((l) => l.name === centerLabel) || 0
  );
  const truncateText = (text, maxLength = 10) => {
    if (!text) return "";
    return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
  };

  const renderCenterText = (index) => {
    const valueText = truncateText(String(data[index] || ""), 10);
    const labelText = truncateText(labels[index]?.name || "", 10);

    return [
      {
        type: "group",
        left: "center",
        top: "center",
        children: [
          {
            type: "text",
            style: {
              text: valueText,
              fontSize: 24,
              fontWeight: "bold",
              fill: "#1B1B1B",
              textAlign: "center",
            },
            top: -10,
          },
          {
            type: "text",
            style: {
              text: labelText,
              fontSize: 10,
              fontWeight: 500,
              fill: colors[index] || "#6B6F7B",
              textAlign: "center",
            },
            top: 10,
          },
        ],
      },
    ];
  };

  const updateCenterText = (index) => {
    if (!chartInstanceRef.current) return;
    chartInstanceRef.current.setOption(
      {
        graphic: renderCenterText(index),
      },
      false
    );
  };

  const initializeChart = () => {
    if (!chartRef.current) return;

    const chart = echarts.init(chartRef.current);
    chartInstanceRef.current = chart;

    chart.setOption({
      color: colors,
      tooltip: {
        trigger: "item",
        formatter: (params) =>
          `${params.marker} ${params.name}: ${params.value}`,
        extraCssText:
          "min-width: auto; width: max-content; padding: 4px 8px; font-size: 12px;",
      },
      legend: { show: false },
      series: [
        {
          name: "",
          type: "pie",
          radius: ["43%", "88%"],
          avoidLabelOverlap: false,
          label: { show: false },
          emphasis: {
            scale: true,
            scaleSize: 10,
          },
          labelLine: { show: false },
          data: labels.map((l, i) => ({
            value: data[i],
            name: l.name,
            itemStyle: { color: colors[i] },
          })),
        },
      ],
      graphic: renderCenterText(selectedIndex),
    });

    chart.on("mouseover", (params) => {
      if (params.dataIndex != null) {
        updateCenterText(params.dataIndex);
      }
    });

    chart.on("click", (params) => {
      if (params.dataIndex != null) {
        setSelectedIndex(params.dataIndex);
        updateCenterText(params.dataIndex);
      }
    });

    window.addEventListener("resize", chart.resize);

    return () => {
      chart.dispose();
      window.removeEventListener("resize", chart.resize);
    };
  };

  useEffect(() => {
    if (!chartRef.current || data.length === 0 || labels.length === 0) return;
    const cleanup = initializeChart();
    return cleanup;
  }, [data, labels, colors]);

  // Reset selected index when centerLabel or labels change
  useEffect(() => {
    const newIndex = labels.findIndex((l) => l.name === centerLabel);
    setSelectedIndex(newIndex >= 0 ? newIndex : 0);
  }, [labels, centerLabel]);

  useEffect(() => {
    updateCenterText(selectedIndex);
  }, [selectedIndex]);

  return (
    <div className="donut-chart-wrapper" style={{ display: "flex" }}>
      <h2 className="margin-title">{title}</h2>
      <div className="wrapper_graph_legend">
        <div className="donut-chart-container">
          <div
            id={chartId}
            ref={chartRef}
            style={{ width: 202, height: 202 }}
          />
        </div>

        <div
          className="legend-grid"
          // style={{
          //   display: "grid",
          //   gridTemplateColumns: "repeat(2, auto)",
          //   gap: "10px 20px",
          //   fontSize: "13px",
          //   alignContent: "center",
          // }}
        >
          {labels.map((item, index) => (
            <div
              key={item.code}
              className="item_box"
              onClick={() => {
                setSelectedIndex(index);
                updateCenterText(index);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                height: "15px",
                cursor: "pointer",
                fontWeight: selectedIndex === index ? "bold" : "normal",
                color: selectedIndex === index ? "#1F77F5" : "#000",
              }}
            >
              <div
                style={{
                  width: "10px",
                  height: "10px",
                  backgroundColor: colors[index],
                  borderRadius: "2px",
                }}
              />
              <strong>
                {/* {item.code} */}
                {shouldTruncate && item.code?.length > 8 ? (
                  <Tooltip title={item?.code}>
                    <strong>{item?.code?.slice(0, 4) + "..."} </strong>
                  </Tooltip>
                ) : (
                  item?.code
                )}
              </strong>
              <span>
                {item.name?.length > 10 ? (
                  <Tooltip title={item?.name}>
                    <span>{item?.name?.slice(0, 10) + "..."}</span>
                  </Tooltip>
                ) : (
                  item?.name
                )}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DonutChart;
